import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';

import { type FishGroupNameOperation, suggestFishGroupName } from './suggestFishGroupName';

interface Props {
    /** Cage the new fish group will live in. */
    cageName: string | null | undefined;
    operation: FishGroupNameOperation;
    /** The season segment of the stocking the group joins; today's when absent. */
    season?: string | null;
    /** Names already used at the site; a clash gets the next free `-2`, `-3`, ... */
    existingNames?: readonly string[];
    /** Writes the suggestion into whichever input this button sits next to. */
    onApply: (name: string) => void;
}

/**
 * Small "Suggest" affordance placed next to a fish-group name input.
 *
 * It owns nothing but the click: the name itself comes from
 * `suggestFishGroupName`, so every button in the feature suggests the same way
 * and the rule stays swappable in one file. Disabled (rather than silently
 * doing nothing) while there is no cage to build a name from.
 */
export default function SuggestNameButton(props: Props) {
    const { cageName, operation, season, existingNames, onApply } = props;
    const { t } = useTranslation('fishGroups');

    const suggestion = suggestFishGroupName({ cageName, operation, season, existingNames });

    return (
        <Button
            variant="link"
            type="button"
            disabled={suggestion === null}
            onClick={() => {
                if (suggestion !== null) onApply(suggestion);
            }}>
            {t('actions.naming.suggest')}
        </Button>
    );
}
